import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { fetchCustomerSelectOptions } from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createDirectSnSale } from '#/api/crmMgmt/snSalesWarranty';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import {
  DIRECT_SALE_FORM_DEFAULT_VALUES,
  SN_WARRANTY_SURFACE_CLASS,
  WARRANTY_POLICY_OPTIONS,
  buildDirectSalePayload,
  directSaleFormSchema,
  getSnSalesWarrantyErrorMessage,
} from '#/modules/crmMgmt/salesOrder/helper/snSalesWarranty';
import { lookupQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/** 直接售出：不建立銷貨單、不改庫存；客戶須等於序號登錄的客戶。 */
const SnDirectSaleSection = ({ onSold }) => {
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(directSaleFormSchema),
    defaultValues: DIRECT_SALE_FORM_DEFAULT_VALUES,
  });
  const warrantyPolicy = useWatch({ control, name: 'warrantyPolicy' });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const saleMutation = useMutation({
    mutationFn: (values) => createDirectSnSale(buildDirectSalePayload(values)),
  });

  async function onSubmit(values) {
    try {
      const result = await saleMutation.mutateAsync(values);
      toast.success(result?.message || '已直接售出');
      reset(DIRECT_SALE_FORM_DEFAULT_VALUES);
      onSold?.(values.serialNumber);
    } catch (error) {
      // 409 保留使用者輸入，讓使用者修正後重送
      toast.error(getSnSalesWarrantyErrorMessage(error, '直接售出失敗'));
    }
  }

  return (
    <form
      className={SN_WARRANTY_SURFACE_CLASS}
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <p className="mb-4 text-sm text-gray-600">
        不建立銷貨單、不改庫存。客戶必須與序號登錄的客戶相同；三年、五年到期日由系統依銷售日計算。
      </p>
      <div className={cn('grid grid-cols-1 gap-3', '@xl/sheet:grid-cols-2')}>
        <FloatingInput
          id="serialNumber"
          type="text"
          label="完整序號"
          register={register}
          errors={errors}
          autoComplete="off"
        />
        <FloatingSelect
          id="customerId"
          label="客戶"
          options={customerOptionsQuery.data ?? []}
          control={control}
          errors={errors}
          isDisabled={customerOptionsQuery.isLoading}
          noOptionsMessage="無可用客戶"
        />
        <FloatingDateInput
          id="salesDate"
          label="銷售日"
          control={control}
          errors={errors}
        />
        <FloatingSelect
          id="warrantyPolicy"
          label="保固政策"
          options={WARRANTY_POLICY_OPTIONS}
          control={control}
          errors={errors}
        />
        {warrantyPolicy === 'CUSTOM' ? (
          <FloatingDateInput
            id="warrantyExpiryDate"
            label="保固迄日"
            control={control}
            errors={errors}
          />
        ) : null}
      </div>
      <div className="mt-2 flex justify-end">
        <Button type="submit" disabled={saleMutation.isPending}>
          {saleMutation.isPending ? '處理中…' : '直接售出'}
        </Button>
      </div>
    </form>
  );
};

SnDirectSaleSection.propTypes = {
  onSold: PropTypes.func,
};

export default SnDirectSaleSection;
